import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const roleColors: Record<string, string> = {
  system: '#534AB7', hotel: '#0F6E56', admin_agent: '#185FA5',
  sub_agent: '#BA7517', direct_agent: '#BA7517', affiliate: '#993C1D', customer: '#A32D2D',
};

const roleLabels: Record<string, string> = {
  system: 'Superadmin', hotel: 'Hotel', admin_agent: 'Admin Agent',
  sub_agent: 'Sub-Agent', direct_agent: 'Direct Agent', affiliate: 'Affiliate', customer: 'Customer',
};

const navItems: Record<string, { label: string; path: string }[]> = {
  system: [
    { label: 'Dashboard', path: '/superadmin' },
    { label: 'Hotels', path: '/superadmin/hotels' },
    { label: 'Users', path: '/superadmin/users' },
    { label: 'Bookings', path: '/superadmin/bookings' },
    { label: 'Wallets', path: '/superadmin/wallets' },
    { label: 'Audit Log', path: '/superadmin/audit' },
  ],
  hotel: [
    { label: 'Dashboard', path: '/hotel' },
    { label: 'Inventory', path: '/hotel/inventory' },
    { label: 'Rate Plans', path: '/hotel/rates' },
    { label: 'Bookings', path: '/hotel/bookings' },
    { label: 'Wallet', path: '/hotel/wallet' },
  ],
  admin_agent: [
    { label: 'Dashboard', path: '/agent' },
    { label: 'Search', path: '/agent/search' },
    { label: 'Sub-Agents', path: '/agent/sub-agents' },
    { label: 'Markup Rules', path: '/agent/markup' },
    { label: 'Bookings', path: '/agent/bookings' },
    { label: 'Wallet', path: '/agent/wallet' },
  ],
  sub_agent: [
    { label: 'Dashboard', path: '/agent' },
    { label: 'Search & Book', path: '/agent/search' },
    { label: 'Bookings', path: '/agent/bookings' },
    { label: 'Markup Rules', path: '/agent/markup' },
    { label: 'Wallet', path: '/agent/wallet' },
  ],
  direct_agent: [
    { label: 'Dashboard', path: '/agent' },
    { label: 'Search & Book', path: '/agent/search' },
    { label: 'Bookings', path: '/agent/bookings' },
    { label: 'Markup Rules', path: '/agent/markup' },
    { label: 'Wallet', path: '/agent/wallet' },
  ],
  customer: [
    { label: 'My Bookings', path: '/customer/bookings' },
    { label: 'Wallet', path: '/customer/wallet' },
  ],
};

interface LayoutProps { children: React.ReactNode; }

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  if (!user) return null;
  const items = navItems[user.role] || [];
  const accentColor = roleColors[user.role] || '#534AB7';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>
      <aside style={{ width: 220, background: '#fafaf9', borderRight: '0.5px solid rgba(0,0,0,0.1)', padding: '0', flexShrink: 0 }}>
        <div style={{ padding: '20px 16px 16px', borderBottom: '0.5px solid rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: accentColor, marginBottom: 2 }}>Ortasys</div>
          <div style={{ fontSize: 11, color: '#888780' }}>Distribution Control System</div>
        </div>
        <div style={{ padding: '12px 8px' }}>
          {items.map(item => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} style={{
                display: 'block', padding: '8px 10px', borderRadius: 8, fontSize: 13,
                color: active ? '#1a1a1a' : '#5F5E5A', fontWeight: active ? 500 : 400,
                background: active ? '#fff' : 'transparent',
                border: active ? '0.5px solid rgba(0,0,0,0.1)' : '0.5px solid transparent',
                textDecoration: 'none', marginBottom: 2,
              }}>{item.label}</Link>
            );
          })}
        </div>
        <div style={{ position: 'absolute', bottom: 0, width: 220, padding: '12px 16px', borderTop: '0.5px solid rgba(0,0,0,0.08)', background: '#fafaf9' }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2 }}>{user.name}</div>
          <div style={{ fontSize: 11, color: '#888780', marginBottom: 8 }}>{roleLabels[user.role]}</div>
          <button onClick={() => { logout(); navigate('/login'); }} style={{
            width: '100%', padding: '6px', borderRadius: 6, border: '0.5px solid rgba(0,0,0,0.15)',
            background: 'transparent', fontSize: 12, cursor: 'pointer', color: '#5F5E5A',
          }}>Logout</button>
        </div>
      </aside>
      <main style={{ flex: 1, padding: '24px 28px', background: '#f8f8f7', overflowY: 'auto' }}>
        {children}
      </main>
    </div>
  );
};
